import { DisabledByDefaultRounded } from "@mui/icons-material";
import { IconButton, TableCell, TableFooter, TableRow, Typography } from "@mui/material";
import { DataTable, DataTableClasses, DataTableHead, type DataTableProps, FunctionField, useRecordContext } from "react-admin";
import { OfferVariantIcon, ReferenceField, SatisfactionInput, StepInput } from "../../components";
import { useAccount, useIsSmall, useOrderItem } from "../../hooks";
import { fullName, type OfferVariant, type Order, type OrderDetail, type OrderItem, orderItemToOfferVariant } from "../../models";
import { OrderDetailGrid } from "../order_detail/grid";

export const OrderItemGrid = ({
    editable,
    first,
    ...rest
}: DataTableProps<OrderItem> & { first?: JSX.Element; editable?: boolean }): JSX.Element => {
    const record = useRecordContext<Order>();
    const { id: accountId } = useAccount();
    const isSmall = useIsSmall();
    const currency = "USD";
    const span = (first ? 2 : 0) + (isSmall ? -2 : 0);
    return (
        <DataTable<OrderItem>
            rowClick={false}
            sx={{ width: "100%" }}
            head={x => (
                <DataTableHead
                    // @ts-expect-error asd
                    first={first}
                    {...x}
                />
            )}
            foot={() => (
                <TableFooter>
                    <TableRow>
                        <TableCell className={DataTableClasses.headerCell} colSpan={3 + span}>
                            <Typography variant="h6">∑</Typography>
                        </TableCell>
                        <TableCell className={DataTableClasses.headerCell} colSpan={-1 + span}>
                            <Typography variant="h6">{record?.total?.toLocaleString([], { style: "currency", currency })}</Typography>
                        </TableCell>
                    </TableRow>
                </TableFooter>
            )}
            bulkActionButtons={false}
            isRowExpandable={x => (x.details?.length ?? 0) > 1}
            expand={<OrderDetailGrid bulkActionButtons={false} />}
            expandSingle
            {...rest}
        >
            <DataTable.Col<OrderItem> label="Item">
                <ReferenceField<OrderItem, OfferVariant>
                    map={orderItemToOfferVariant}
                    source="id"
                    reference="offer"
                    render={x => (
                        <>
                            <OfferVariantIcon {...x} /> {fullName(x)}
                            {!editable && x.offerId !== 0 ? ` x${(x as any)["quantity"]}` : ""}
                        </>
                    )}
                />
            </DataTable.Col>
            {!editable && <DataTable.Col<OrderItem> 
                label="Rating" 
                render={() => <SatisfactionInput<OrderDetail> disabled={!(record?.status === "Shipped" && record.buyerId === accountId)} 
                source="satisfaction" />} 
            />} 
            {editable && <DataTable.Col<OrderItem> label="Count">
                <ItemCount editable={editable} />
            </DataTable.Col>}
            {!isSmall && (
                <DataTable.Col<OrderItem> label="Price" render={x => `${(x.price + x.discount + x.vat).toLocaleString([], { style: "currency", currency })}`} />
            )}
            <DataTable.Col<OrderItem>
                label="Subtotal"
                render={x => `${((x.price + x.discount + x.vat) * x.quantity).toLocaleString([], { style: "currency", currency })}`}
            />
            {record?.status === "Temporary" && !isSmall && (
                <DataTable.Col<OrderItem>>
                    <RemoveItem />
                </DataTable.Col>
            )}
        </DataTable>
    );
};

const ItemCount = ({ editable }: { editable?: boolean }): JSX.Element => {
    const { buyerId, sellerId, orderId, itemId, variantId, quantity } = useRecordContext<OrderItem>() ?? ({} as OrderItem);
    const [oi, setOi] = useOrderItem({ buyerId, sellerId, orderId, itemId, variantId, quantity } as any);
    if (itemId === 0) {
        return <></>;
    }
    return editable ? (
        <FunctionField<OrderItem>
            render={x => (
                <>
                    <StepInput value={oi} sx={{ maxWidth: 120, "& .MuiInputBase-input": { textAlign: "center" } }} onUpdate={x => setOi(x)} />
                    {x.details.length === 1 && x.details[0].quantity > 1 ? (
                        <Typography variant="body2" sx={{ display: "inline-block", paddingTop: 1 }}>
                            x {x.details[0].quantity}
                        </Typography>
                    ) : (
                        ""
                    )}
                </>
            )}
        />
    ) : (
        <FunctionField<OrderItem>
            sortBy="quantity"
            render={x => (x.details.length === 1 && x.details[0].quantity > 1 ? `${x.quantity} x ${x.details[0].quantity}` : x.quantity)}
        />
    );
};

const RemoveItem = (): JSX.Element => {
    const { buyerId, sellerId, orderId, itemId, variantId, quantity } = useRecordContext<OrderItem>() ?? ({} as OrderItem);
    const [_, setOi] = useOrderItem({ buyerId, sellerId, orderId, itemId, variantId, quantity } as any);
    return (
        <FunctionField<OrderItem>
            render={x => {
                return x.itemId !== 0 ? (
                    <IconButton onClick={() => setOi(0)}>
                        <DisabledByDefaultRounded />
                    </IconButton>
                ) : (
                    <></>
                );
            }}
        />
    );
};
